import type { EnvironmentId, WorldSourceFolderDetail } from "@cleo/contracts";

import type { WorldWorkspaceBindings } from "./worldWorkspaceBinding";

/** WB6: each listed path's person-given name and description, keyed by the saved path. */
export type WorldSourceFolderDetails = Readonly<Record<string, WorldSourceFolderDetail>>;

/**
 * One World's device-local workspace binding projected for Settings: the
 * bound default project (a user-selected local folder, code or not, 2026-08-28 ruling) and
 * the non-code Files root. `worldName` is null when the sign-in snapshot
 * carries no live grant for the key.
 */
export interface BoundWorldBindingSettingsRow {
  readonly kind: "bound";
  readonly worldKey: string;
  readonly worldName: string | null;
  readonly environmentId: EnvironmentId;
  /** The ordered folder list; null when this World never wrote one. */
  readonly folders: ReadonlyArray<string> | null;
  readonly projectId: string | null;
  readonly projectTitle: string | null;
  readonly filesRoot: string | null;
  /** WB5: the person's explicit Codebases and Files folder lists; empty lists stay empty.
   * WB6: `details` carries each listed path's person-given name and description. */
  readonly sourceFolders: {
    readonly codebase: ReadonlyArray<string>;
    readonly files: ReadonlyArray<string>;
    readonly details?: WorldSourceFolderDetails | undefined;
  } | null;
}

/** A live World membership with no non-code Files binding row yet. */
export interface UnboundWorldBindingSettingsRow {
  readonly kind: "unbound";
  readonly worldKey: string;
  readonly worldName: string;
  /** The device-local environment where the first binding can be written. */
  readonly environmentId: EnvironmentId | null;
  readonly folders: null;
  readonly projectId: null;
  readonly projectTitle: null;
  readonly filesRoot: null;
  readonly sourceFolders: null;
}

export type WorldWorkspaceBindingSettingsRow =
  | BoundWorldBindingSettingsRow
  | UnboundWorldBindingSettingsRow;

export interface WorldBindingSettingsProject {
  readonly environmentId: EnvironmentId;
  readonly id: string;
  readonly title: string;
  readonly workspaceRoot: string;
}

/**
 * The Settings projection of every World → local workspace binding this
 * client can see. Live Worlds come first, followed by orphaned local rows.
 */
export function projectWorldWorkspaceBindingSettingsRows(input: {
  readonly worlds: ReadonlyArray<{ readonly worldKey: string; readonly name: string }>;
  readonly bindingsByEnvironment: ReadonlyMap<EnvironmentId, WorldWorkspaceBindings>;
  readonly projects: ReadonlyArray<WorldBindingSettingsProject>;
  /** The one local environment an unbound membership may be bound into. */
  readonly unboundEnvironmentId: EnvironmentId | null;
}): ReadonlyArray<WorldWorkspaceBindingSettingsRow> {
  const worldNames = new Map(input.worlds.map((world) => [world.worldKey, world.name]));
  const projectTitle = (environmentId: EnvironmentId, projectId: string): string | null =>
    input.projects.find(
      (candidate) => candidate.environmentId === environmentId && candidate.id === projectId,
    )?.title ?? null;

  const boundRowsByWorld = new Map<string, BoundWorldBindingSettingsRow[]>();
  for (const [environmentId, bindings] of input.bindingsByEnvironment) {
    for (const [worldKey, binding] of Object.entries(bindings)) {
      const projectId = binding.defaultExecutionProjectId ?? null;
      const filesRoot = binding.filesRoot?.trim() || null;
      const folders = binding.folders ?? null;
      const sourceFolders = binding.sourceFolders
        ? {
            codebase: binding.sourceFolders.codebase,
            files: binding.sourceFolders.files,
            ...(binding.sourceFolders.details === undefined
              ? {}
              : { details: binding.sourceFolders.details }),
          }
        : null;
      if (projectId === null && filesRoot === null && folders === null && sourceFolders === null)
        continue;
      const rows = boundRowsByWorld.get(worldKey) ?? [];
      rows.push({
        kind: "bound",
        worldKey,
        worldName: worldNames.get(worldKey) ?? null,
        environmentId,
        folders,
        projectId,
        projectTitle: projectId === null ? null : projectTitle(environmentId, projectId),
        filesRoot,
        sourceFolders,
      });
      boundRowsByWorld.set(worldKey, rows);
    }
  }

  const rows: WorldWorkspaceBindingSettingsRow[] = [];
  for (const world of input.worlds) {
    const bound = boundRowsByWorld.get(world.worldKey);
    if (bound === undefined) {
      rows.push({
        kind: "unbound",
        worldKey: world.worldKey,
        worldName: world.name,
        environmentId: input.unboundEnvironmentId,
        folders: null,
        projectId: null,
        projectTitle: null,
        filesRoot: null,
        sourceFolders: null,
      });
      continue;
    }
    rows.push(...bound);
    boundRowsByWorld.delete(world.worldKey);
  }
  for (const worldKey of [...boundRowsByWorld.keys()].toSorted()) {
    rows.push(...(boundRowsByWorld.get(worldKey) ?? []));
  }
  return rows;
}
